import React, { useState, useMemo , useEffect, useCallback } from "react";
import { Select, Button, Spinner } from "flowbite-react";
import { AiOutlineDownload } from "react-icons/ai";
import LeaderboardStaff from "../../Components/LeaderBoardStaff.jsx";
import { useGetLeaderboardQuery } from "../../../../graphql/generated.tsx";
import formatDate from "../../Helpers/formatDate.jsx";
import { toast } from "../../../../toast";
import LightSpinner from "../../../../components/LightSpinner.jsx";

const LEADERBOARD_PAGE_SIZE = 20;

const BatchAnalysisTab = ({ availableClusters, campusName }) => {
  const [selectedCluster, setSelectedCluster] = useState(availableClusters?.[0]?.id);
  const [filter, setFilter] = useState("custom");
  const [downloading, setDownloading] = useState(false);
   const [Isload, setIsload] = useState(true);
  const [currentLeaderboardPage, setCurrentLeaderboardPage] = useState(1);
  const [hasMoreLeaderboard, setHasMoreLeaderboard] = useState(true);
  const [isFetchingMoreLeaderboard, setIsFetchingMoreLeaderboard] = useState(false);

   useEffect(()=>{
    setTimeout(()=>{
      setIsload(false);
    },2000);
  },[]);
  

  const clusterId = selectedCluster || availableClusters?.[0]?.id;

  const {
    data: leaderboardData,
    loading: leaderboardLoading,
    error: leaderboardError,
    refetch: leaderboardRefetch,
    fetchMore: fetchMoreLeaderboard,
  } = useGetLeaderboardQuery({
    variables: {
      campusName,
      clusterId,
      filter,
      page: 1,
      limit: LEADERBOARD_PAGE_SIZE,
    },
    notifyOnNetworkStatusChange: true,
  });

  const loadNextLeaderboardPage = useCallback(async () => {
    if (isFetchingMoreLeaderboard || !hasMoreLeaderboard) return;
    setIsFetchingMoreLeaderboard(true);
    const nextPage = currentLeaderboardPage + 1;
    let incomingCount = 0;
    let appendedCount = 0;

    try {
      await fetchMoreLeaderboard({
        variables: {
          campusName,
          clusterId,
          filter,
          page: nextPage,
          limit: LEADERBOARD_PAGE_SIZE,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getLeaderboard?.length) return prev;

          const existing = prev?.getLeaderboard ?? [];
          const incoming = fetchMoreResult.getLeaderboard;
          incomingCount = incoming.length;

          const seen = new Set(
            existing.map((row) => String(row?.id ?? row?.student_id ?? ""))
          );
          const uniqueIncoming = incoming.filter((row) => {
            const key = String(row?.id ?? row?.student_id ?? "");
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          appendedCount = uniqueIncoming.length;

          return {
            ...prev,
            getLeaderboard: [...existing, ...uniqueIncoming],
          };
        },
      });

      if (appendedCount > 0) {
        setCurrentLeaderboardPage(nextPage);
      }
      setHasMoreLeaderboard(
        incomingCount === LEADERBOARD_PAGE_SIZE && appendedCount > 0
      );
    } catch (error) {
      toast.error(error?.message || "Failed to load more leaderboard rows.");
    } finally {
      setIsFetchingMoreLeaderboard(false);
    }
  }, [
    isFetchingMoreLeaderboard,
    hasMoreLeaderboard,
    currentLeaderboardPage,
    fetchMoreLeaderboard,
    campusName,
    clusterId,
    filter,
  ]);

  useEffect(() => {
    setCurrentLeaderboardPage(1);
    const rows = leaderboardData?.getLeaderboard ?? [];
    setHasMoreLeaderboard(rows.length === LEADERBOARD_PAGE_SIZE);
  }, [clusterId, filter, campusName]);

  // leaderboard calculations
  const leaderboard = leaderboardData?.getLeaderboard || [];
  const notStartedStudents = useMemo(
    () => leaderboard.filter((student) => student.totalScore === 0),
    [leaderboard]
  );
  const filteredLeaderboard = useMemo(
    () => leaderboard.filter((student) => student.totalScore > 0),
    [leaderboard]
  );

  const top20Students = useMemo(
    () => filteredLeaderboard.slice(0, 20),
    [filteredLeaderboard]
  );
  const least20Students = useMemo(
    () =>
      filteredLeaderboard.length > 20
        ? filteredLeaderboard.slice(-20)
        : [],
    [filteredLeaderboard]
  );

  const downloadExcel = async () => {
    try {
      setDownloading(true);
      toast.info("Generating Excel file...");
      const headers = [
        "Position",
        "Student Email",
        "Last Submission Date",
        "Total Score",
        "Total Completed Questions",
        "Easy Questions Completed (Course)",
        "Medium Questions Completed (Course)",
        "Hard Questions Completed (Course)",
        "Total Leetcode Questions Completed",
        "Leetcode Easy",
        "Leetcode Medium",
        "Leetcode Hard",
      ];
      const getData = (students) =>
        students.map((student, index) => {
          const leetcodeEasy =
            student.leetcode_data?.find((data) => data.difficulty === "Easy")?.count || 0;
          const leetcodeMedium =
            student.leetcode_data?.find((data) => data.difficulty === "Medium")?.count || 0;
          const leetcodeHard =
            student.leetcode_data?.find((data) => data.difficulty === "Hard")?.count || 0;
          return [
            index + 1,
            student.student.email,
            student.latestSubmissionDate ? formatDate(student.latestSubmissionDate) : "-",
            student.totalScore,
            student.student.completedQuestions?.length || 0,
            student.easyQuestionsCompleted || 0,
            student.mediumQuestionsCompleted || 0,
            student.hardQuestionCompleted || 0,
            leetcodeEasy + leetcodeMedium + leetcodeHard,
            leetcodeEasy,
            leetcodeMedium,
            leetcodeHard,
          ];
        });

      // TODO: Write your Excel export logic here...

      toast.success("Excel file downloaded successfully!");
    } catch (err) {
      toast.error("Failed to download Excel.");
    } finally {
      setDownloading(false);
    }
  };

  const Datepicker = () => (
    <Select
      value={filter}
      onChange={(e) => setFilter(e.target.value)}
      disabled={leaderboardLoading}
    >
      <option value="custom">TILL DATE</option>
      <option value="weekly">PAST 7 DAYS</option>
      <option value="daily">TODAY</option>
      <option value="past10days">PAST 10 DAYS</option>
      <option value="monthly">THIS MONTH</option>
    </Select>
  );

  return (
    <div className="p-4 sm:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4 font-poppins">
        <Select
          value={selectedCluster}
          onChange={(e) => setSelectedCluster(e.target.value)}
          disabled={availableClusters?.length === 1 || leaderboardLoading}
        >
          {availableClusters?.map((cluster) => (
            <option key={cluster.id} value={cluster.id}>
              {cluster.name}
            </option>
          ))}
        </Select>
        <div className="flex items-center gap-5">
          {leaderboard?.length > 0 && <Datepicker />}
          <Button
            onClick={downloadExcel}
            className="bg-primary text-white flex items-center h-[40px] transition-transform hover:scale-105"
            disabled={downloading}
          >
            {downloading ? (
              <>
                <Spinner size="sm" />
                Downloading...
              </>
            ) : (
              <>
                <AiOutlineDownload className="w-4 h-5 mr-2" />
                Download
              </>
            )}
          </Button>
        </div>
      </div>

      {/* {leaderboardLoading && (
        <div className="flex justify-center mt-10">
          <LightSpinner message="Loading leaderboard..." />
        </div>
      )} */}

      {leaderboardError && (
        <div className="mt-4 text-center text-red-500">
          Failed to load leaderboard.
          <Button color="light" onClick={() => leaderboardRefetch()}>
            Retry
          </Button>
        </div>
      )}

      {!leaderboardLoading && leaderboard?.length === 0 && (
        <p className="mt-6 text-center text-gray-500">No leaderboard data available.</p>
      )}

      {leaderboard?.length > 0 && (
        <LeaderboardStaff
          onDownload={downloadExcel}
          clusterId={clusterId}
          campusName={campusName}
          leaderboardError={leaderboardError}
          leaderboardData={leaderboardData}
          onLoadMore={loadNextLeaderboardPage}
          hasMore={hasMoreLeaderboard}
          isFetchingMore={isFetchingMoreLeaderboard}
        />
      )}
    </div>
  );
};

export default BatchAnalysisTab;
